import { memo, useEffect } from 'react';

import { type Board } from '@air/api/types';
import { type TreeItemProps } from '@air/component-tree';
import { Button } from '@air/primitive-button';
import { Spinner } from '@air/primitive-spinner';

import { BoardsTree, type BoardsTreeProps } from '~/components/CurrentWorkspaceNav/BoardsNav/BoardsTree/BoardsTree';
import { BoardsTreeItemContent } from '~/components/CurrentWorkspaceNav/BoardsNav/BoardsTreeItem/BoardsTreeItemContent';
import { DNDMovableToBoardItem } from '~/components/Shared/Drag/DNDMovableToBoardItem/DNDMovableToBoardItem';
import { DNDParentBoard } from '~/components/Shared/Drag/DNDParentBoard';
import { DragType } from '~/components/Shared/Drag/dragTypes';
import { useBoardPermissions } from '~/hooks/useBoardPermissions';
import { useCreateSubBoard } from '~/hooks/useCreateSubBoard';
import { useFetchObjectsPermissions } from '~/hooks/useFetchObjectsPermissions';
import { isAncestorOfCentralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { useSubBoardsList } from '~/swr-hooks/boards/useSubBoardsList';
import { isMobileAgent } from '~/utils/PlatformHelpers';
import {
  canChangeBoardAssetsOrBoardsPosition,
  canCreateBoard,
  canDeleteBoard,
} from '~/utils/permissions/boardPermissions';
import { useAirSelector } from '~/utils/ReduxUtils';

const DRAG_TYPE: Record<
  'favorites' | 'workspace' | 'library',
  DragType.boardNavWorkspace | DragType.boardNavFavorites | DragType.boardNavLibrary
> = {
  library: DragType.boardNavLibrary,
  workspace: DragType.boardNavWorkspace,
  favorites: DragType.boardNavFavorites,
};

export type BoardsTreeItemProps = {
  board: Board;
  boardType: 'favorites' | 'workspace' | 'library';
  enableAutoExpand: boolean;
  index: number;
  isOpen?: TreeItemProps['open'];
  isTouched?: boolean;
  onBoardRearrange: BoardsTreeProps['onBoardRearrange'];
  onOpenChange: ({ id, isAutoExpand, isOpen }: { id: string; isAutoExpand?: boolean; isOpen: boolean }) => void;
  getCanRearrange?: BoardsTreeProps['getCanRearrange'];
};

export const BoardsTreeItem = memo(
  ({
    board,
    boardType,
    index,
    enableAutoExpand,
    isOpen,
    isTouched,
    onBoardRearrange,
    onOpenChange,
    getCanRearrange,
  }: BoardsTreeItemProps) => {
    const { boardPermissions } = useBoardPermissions({
      boardId: board.id,
    });

    const isAncestorOfCurrentBoard = useAirSelector((state) => isAncestorOfCentralizedBoardSelector(state, board.id));

    useEffect(() => {
      if (isTouched) {
        return;
      }

      if (isAncestorOfCurrentBoard && enableAutoExpand) {
        onOpenChange({ id: board.id, isAutoExpand: true, isOpen: true });
      }
    }, [board.id, enableAutoExpand, isAncestorOfCurrentBoard, isTouched, onOpenChange]);

    const {
      data: subBoards = [],
      isLoadingMore,
      hasMore,
      isInitialLoading,
      loadNextPage,
    } = useSubBoardsList({ boardId: board.id, canFetchData: isOpen || false, libraryId: board.library?.id });

    useFetchObjectsPermissions({
      objects: {
        boardIds: subBoards.map((board) => board.id),
      },
    });

    const { createSubBoard } = useCreateSubBoard();

    /**
     * Show 'Create a board' button if the user canEdit, list is expanded, boards aren't being fetch
     * and or were expanded on mount and there are no subboards.
     */
    const shouldShowCreateButton =
      canCreateBoard(boardPermissions) && isOpen && subBoards.length === 0 && !isInitialLoading && !isLoadingMore;

    /**
     * Show subboards tree if the list is expanded and there are subboards for the expanded board.
     */
    const shouldShowSubBoards = isOpen && !!subBoards.length;

    /**
     * Show 'Show more' button when list is expanded, boards aren't being fetched and there
     * are more subboards that can be shown.
     */
    const shouldShowMoreButton = isOpen && hasMore && !isInitialLoading;

    return (
      <DNDMovableToBoardItem
        id={board.id}
        item={{
          type: DRAG_TYPE[boardType],
          board,
        }}
        index={index}
        enabled={!isMobileAgent}
        data-testid="BOARDS_TREE_ITEM_DND_MOVABLE_TO_BOARD_ITEM"
      >
        <div data-testid="BOARDS_TREE_ITEM_CONTENT_CONTAINER" className="relative">
          <DNDParentBoard dropLocation="board" board={board}>
            {isInitialLoading && isOpen && (
              <div className="absolute top-2 left-2 flex">
                <Spinner className="size-4 ads:text-icon-default text-gray-10" />
              </div>
            )}
            <BoardsTreeItemContent
              board={board}
              // biome-ignore lint/performance/noJsxPropsBind: open-change handler closes over board id for tree expansion
              onOpenChange={(isOpen) => {
                onOpenChange({ id: board.id, isOpen });
              }}
              open={isOpen}
            />
            {shouldShowCreateButton && (
              <Button
                data-testid="EMPTY_BOARDS_TREE_ITEM_CREATE_SUB_BOARD_BUTTON"
                // biome-ignore lint/performance/noJsxPropsBind: create sub-board click uses board context without memoizing
                onClick={() =>
                  createSubBoard({ selectedParentBoard: board, trackLocation: 'side-nav-extension-item-sub-button' })
                }
                color="gray"
                appearance="ghost"
                size="small"
                className="ml-6 inline-block truncate rounded-2 ads:text-metadata text-gray-10"
              >
                Create New Sub-Board
              </Button>
            )}
          </DNDParentBoard>
        </div>

        {shouldShowSubBoards && (
          <div className="ml-3">
            <BoardsTree
              boardType={boardType}
              parentBoardId={board.id}
              boards={subBoards}
              enableAutoExpand={enableAutoExpand}
              onBoardRearrange={onBoardRearrange}
              /**
               * Use the passed getCanRearrange function if available (e.g., for library boards with custom sort),
               * otherwise fall back to the permission check for workspace/favorite boards
               */
              getCanRearrange={
                getCanRearrange ??
                (() => canDeleteBoard(boardPermissions) || canChangeBoardAssetsOrBoardsPosition(boardPermissions))
              }
            />
          </div>
        )}

        {shouldShowMoreButton && (
          <Button
            color="gray"
            appearance="ghost"
            className="ml-9"
            isLoading={isLoadingMore}
            size="small"
            // biome-ignore lint/performance/noJsxPropsBind: loadNextPage wrapper keeps button API unchanged
            onClick={() => loadNextPage()}
          >
            Show more
          </Button>
        )}
      </DNDMovableToBoardItem>
    );
  },
);

BoardsTreeItem.displayName = 'BoardsTreeItem';
